import type { Ref } from "vue";
import { ref } from "vue";

interface Hooks {
  onEnd?: VoidFunction;
  onStart?: VoidFunction;
}

/**
 * Creates a transition utility for a given element.
 *
 * @param el - The element to transition.
 * @returns An object containing the `transition` functions.
 */
export function useTransition(el: Ref<HTMLElement | null>) {
  const classes = ref<Record<string, true>>({});

  async function transition(cssClass: string, hooks: Hooks = {}) {
    classes.value[cssClass] = true;

    const handleTransitionEnd = () => {
      hooks.onEnd?.();
      delete classes.value[cssClass];
    };
    const handleTransitionStart = () => {
      hooks.onStart?.();
    };

    await new Promise<void>((resolve) => {
      el.value?.addEventListener(
        "transitionend",
        () => {
          handleTransitionEnd();
          resolve();
        },
        { once: true },
      );
      el.value?.addEventListener("transitionstart", handleTransitionStart, { once: true });
    });
  }

  return { transition, classes };
}
